import { TextField, YesNo } from "@/components/apply/Steps";
import { Checkbox } from "@/components/ui/checkbox";
import { formatPhone } from "@/lib/applyApi";

const fromContact = (c) => ({ trainer_name: c?.full_name || "", trainer_phone: c?.phone || "", trainer_email: c?.email || "" });

// Per-horse trainer: Yes/No, "I am the trainer" (uses the seller's contact details), or another trainer's details.
export function HorseTrainerFields({ horse, set, contact, errors = {}, ownerInvite = false }) {
  const has = horse.has_trainer || (horse.trainer_name ? "yes" : "");
  const me = !!horse.trainer_is_seller;
  const access = horse.trainer_access === "yes";
  // New trainer entries default access to Yes; a saved/manual choice is kept.
  const dflt = ownerInvite && horse.trainer_access !== "yes" && horse.trainer_access !== "no" ? { trainer_access: "yes" } : {};
  const pick = (v) => set({ ...horse, has_trainer: v, ...(v === "no" ? { trainer_is_seller: false, trainer_access: "", trainer_name: "", trainer_phone: "", trainer_email: "" } : dflt) });
  const toggleMe = (on) => set({ ...horse, trainer_is_seller: on, ...(on ? { ...fromContact(contact), trainer_access: "" } : { trainer_name: "", trainer_phone: "", trainer_email: "", ...dflt }) });
  return (
    <div data-testid="apply-trainer" className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">{ownerInvite ? "Trainer" : "About this horse"}</p>
      <YesNo id="apply-has-trainer" label={ownerInvite ? "Do you have a trainer working with this horse?" : "Is there a trainer associated with this horse?"} value={has} onChange={pick} error={errors.has_trainer} />
      {has === "yes" && contact?.is_trainer !== "no" && (
        <label className="flex items-center gap-2 text-sm font-medium min-h-[44px]">
          <Checkbox data-testid="apply-trainer-is-me" checked={me} onCheckedChange={(v) => toggleMe(!!v)} /> I am this horse's trainer (use my account details)
        </label>
      )}
      {has === "yes" && me && (
        <p data-testid="apply-trainer-me-summary" className="text-sm rounded-lg bg-stone-50 border border-stone-200 p-3">
          Using your account details: <strong>{contact?.full_name}</strong> · {contact?.phone}{contact?.email ? ` · ${contact.email}` : ""}
        </p>
      )}
      {has === "yes" && !me && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField id="apply-trainer-name" label="Trainer Name" required value={horse.trainer_name} onChange={(v) => set({ ...horse, trainer_name: v })} error={errors.trainer_name} autoComplete="off" />
          <TextField id="apply-trainer-phone" label="Trainer Phone Number" required type="tel" inputMode="tel" value={horse.trainer_phone} onChange={(v) => set({ ...horse, trainer_phone: v })}
            error={errors.trainer_phone} hint="Include area code, e.g. +1 403 555 0123" autoComplete="off" onBlur={() => horse.trainer_phone && set({ ...horse, trainer_phone: formatPhone(horse.trainer_phone) })} />
          <TextField id="apply-trainer-email" label="Trainer Email Address" required={access} type="email" inputMode="email" value={horse.trainer_email} onChange={(v) => set({ ...horse, trainer_email: v })}
            error={errors.trainer_email} hint={access ? "We'll send the invitation here." : "Optional"} autoComplete="off" />
        </div>
      )}
      {ownerInvite && has === "yes" && !me && (
        <div data-testid="apply-trainer-access" className="rounded-xl border border-stone-200 bg-white p-3 space-y-2">
          <YesNo id="apply-trainer-access" label="Would you like your trainer to have access to manage this horse's advertisement?" value={horse.trainer_access || ""} error={errors.trainer_access}
            onChange={(v) => set({ ...horse, trainer_access: v })} />
          <p data-testid="apply-trainer-access-note" className="text-xs text-stone-500">
            {access ? "When you submit, we'll email your trainer a secure sign-in invitation. They can help with details, photos and the Ad Template. Only you can sign the contract, and you can remove their access any time."
              : "Your trainer's details are saved for this horse only. They won't get a login."}
          </p>
        </div>
      )}
    </div>
  );
}

export function validateTrainer(h, validPhone, ownerEmail) {
  const e = {};
  const has = h.has_trainer || (h.trainer_name ? "yes" : "");
  if (has !== "yes" && has !== "no") e.has_trainer = "Please tell us whether a trainer is associated with this horse.";
  if (has === "yes" && !h.trainer_is_seller) {
    if (!h.trainer_name?.trim()) e.trainer_name = "Please enter the trainer's name.";
    if (!h.trainer_phone?.trim()) e.trainer_phone = "Please enter the trainer's phone number.";
    else if (!validPhone(h.trainer_phone)) e.trainer_phone = "Please enter a valid phone number (10–15 digits).";
    if (ownerEmail !== undefined && h.trainer_access !== "yes" && h.trainer_access !== "no") e.trainer_access = "Please choose whether your trainer should have access.";
    if (h.trainer_access === "yes" && !h.trainer_email?.trim()) e.trainer_email = "Please enter your trainer's email so we can send the invitation.";
    else if (h.trainer_email?.trim() && !/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(h.trainer_email.trim())) e.trainer_email = "Please enter a valid email, or leave it blank.";
    else if (h.trainer_access === "yes" && ownerEmail && h.trainer_email.trim().toLowerCase() === ownerEmail.trim().toLowerCase()) e.trainer_email = "Your trainer's email must be different from yours.";
  }
  return e;
}

// Read-only trainer reference for the contract page (not part of the signed agreement).
export function TrainerReference({ t }) {
  if (!t) return null;
  return (
    <div data-testid="contract-trainer-reference" className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm">
      <p className="font-semibold text-stone-900">Trainer (for reference)</p>
      <p className="mt-0.5">{t.name}{t.is_seller ? " (you)" : ""} · {t.phone}{t.email ? ` · ${t.email}` : ""}</p>
      <p className="mt-1 text-xs text-stone-500">Not part of the signed agreement. Update it in your horse's Ad Template.</p>
    </div>
  );
}
